// medplum-live.js — the LIVE layer for the online-primary redesign (main-2):
// (1) stale-while-revalidate (SWR) caching so a screen paints instantly from
//     the last authorized read while a fresh read runs in the background, and
// (2) Medplum WebSocket subscriptions that NUDGE a screen to re-read when a
//     resource it cares about changes on the server.
//
// Both are additive and safe-by-construction:
//   - SWR only ever stores what an AUTHORIZED loader returned for THIS identity
//     (the cache key includes the logged-in user's id — see cacheKeyFor), so a
//     doctor can never paint a receptionist's cached board and vice-versa. No
//     app-side per-row re-check is added or needed; the cache inherits the RLS
//     scoping of the read that produced it (AGENTS.md §2, redesign invariant).
//   - The WS line is a NUDGE only: an incoming notification just re-triggers the
//     screen's EXISTING refetch (loadXFromMedplum → setState), exactly like the
//     SSE line in opd-events.js did. It never carries render data. So a dropped
//     or unavailable socket degrades to "updates a little slower" (each screen
//     keeps its own poll), never to a broken screen.
//
// Fail loud, no fake liveness: if the socket cannot connect we console.warn ONCE
// and stay static — no retry storm layered on top of MedplumClient's own
// ReconnectingWebSocket, no pretending we're live when we're not.
import { getMedplum, activeLoginProfileId, onResourceModified } from './medplum-client.js';
import { OPD_LOCAL_EVENT } from './opd-events.js';
import { isCapacitorNative } from './capacitor-env.js';
import { createSqliteReadCacheStorage } from './sqlite-store.js';

// The storage primitive behind the SWR read cache. On the web this IS the
// browser/WebView localStorage — readCache/writeCache below behave byte-for-byte
// as before. On a native device we swap in an encrypted-SQLite-backed,
// localStorage-SHAPED synchronous KV so cached patient data (PHI) never lands in
// the WebView's plaintext localStorage. The swap is done SYNCHRONOUSLY at module
// load (createSqliteReadCacheStorage returns immediately; its encrypted DB opens
// in the background and its getItem simply MISSES until warm — a safe SWR cache
// miss), so localStorage is never touched on device. This is the one line
// readCache/writeCache changed: `window.localStorage` → `swrStorage`.
let swrStorage = isCapacitorNative()
  ? createSqliteReadCacheStorage()
  : ((typeof window !== 'undefined' && window.localStorage) ? window.localStorage : null);

// Test-only seam: read/override the storage primitive so the platform-selection
// test can confirm the web default is the real localStorage, and the read-cache
// test can drive readCache/writeCache/swrLoad against the SQLite-backed KV
// without a device. Never called by the app.
export function __swrStorageForTest() { return swrStorage; }
export function __setSwrStorageForTest(s) { swrStorage = s; }

// ────────────────────────────────────────────────────────────────────────────
// 1. Stale-while-revalidate cache
// ────────────────────────────────────────────────────────────────────────────

const SWR_PREFIX = 'healther.swr.';

// Cache envelope schema version. BUMP this whenever the SHAPE of any cached DTO
// changes (a loader starts returning {page,pageSize,total}-shaped data, a field
// is renamed, etc.) so old entries written by a previous build are silently
// ignored instead of painting a mismatched shape into the current UI. A version
// mismatch is treated as a plain cache MISS — the next authorized read overwrites
// the stale entry, no error, no migration code. Legacy envelopes written before
// versioning existed carry no `v` and are likewise treated as a miss.
const SCHEMA_VERSION = 3;

// The cache key MUST be per-identity so one user's cached payload can never be
// painted for another (no cross-user leakage). `identity` is the medplum-client
// identity the loader logs in as ('doctor' | 'receptionist'); we fold in that
// identity's real logged-in profile id too, so even if two people share a
// browser + identity slot on this demo harness, a re-login to a different user
// gets a different key.
function cacheKeyFor(route, identity, profileId) {
  return SWR_PREFIX + identity + '.' + (profileId || 'anon') + '.' + route;
}

function readCache(key) {
  try {
    const raw = swrStorage.getItem(key);
    if (!raw) return null;
    const wrapped = JSON.parse(raw);
    // Shape guard: only accept our own {v, storedAt, payload} envelope AT the
    // current schema version. A missing/older `v` (legacy or a stale build's
    // entry) → treat as a cache miss so we never paint a mismatched DTO shape.
    if (!wrapped || wrapped.v !== SCHEMA_VERSION
      || typeof wrapped.storedAt !== 'number' || !('payload' in wrapped)) return null;
    return wrapped;
  } catch (_) {
    return null; // corrupt/unavailable storage → treat as cache miss, revalidate.
  }
}

function writeCache(key, payload) {
  try {
    swrStorage.setItem(key, JSON.stringify({ v: SCHEMA_VERSION, storedAt: Date.now(), payload }));
  } catch (_) {
    // Storage full / disabled → SWR silently degrades to always-fresh. Not fatal.
  }
}

// ── Write-driven invalidation (resourceModified → mark boards stale) ─────────
//
// Which board cache keys contain rows of a given resource type. This is the
// INVERSE of the repo's own change→board judgment: bridge.js LIVE_CRITERIA
// (queue/reception/schedule/patients/reports) plus the Tasks/Messaging screens'
// ensureLiveWire criteria. Keep the three in sync when a board's data sources
// change. The consult route ('encounter') is deliberately absent — it is never
// SWR-cached at all (bridge.js SWR_ROUTES).
const WRITE_INVALIDATES = {
  Encounter: ['queue', 'reception', 'schedule', 'patients'],
  Appointment: ['schedule'],
  Patient: ['patients'],
  DocumentReference: ['reports'],
  Task: ['tasks'],
  Communication: ['messaging'],
};

// cacheKeyFor's identity slot. Every current swrLoad caller uses the default
// ('doctor'), but the parameter still exists — mark both slots so a future
// caller passing 'receptionist' is covered too.
const SWR_IDENTITIES = ['doctor', 'receptionist'];

/**
 * THE one-place read-cache invalidation for writes: called (via the
 * onResourceModified fan-out in medplum-client.js) after every successful
 * create/update/patch/delete through THE staff MedplumClient, with the event
 * payload { resourceType, operation, id?, resource? }.
 *
 * What it does: MARKS this user's cached board envelopes for the affected
 * routes `stale: true`. swrLoad then treats a marked hit as FETCH-FIRST — it
 * never paints the pre-write payload when the network can answer, and falls
 * back to the marked payload only when it can't. Marking (not deleting) is
 * deliberate: the SWR cache is ALSO the offline read store for the list boards
 * (bridge.js — "offline resilience on the list boards comes from the SWR
 * cache"), so deleting entries after every write would strip a tablet's
 * offline paint during active use. No SWR key encodes a resource id (keys are
 *  per-user BOARD routes — see cacheKeyFor), so invalidation is by
 * resourceType; the id-level cached read lives in MedplumClient's own request
 * cache, which @medplum/core ≥5.1.28 already invalidates internally on write.
 *
 * Only the CURRENT logged-in user's keys are marked (a write can only have
 * come from their session; other users' entries revalidate on their own).
 * Sync by design — it runs inside the write's event dispatch. On the device
 * store a not-yet-warm mirror simply yields no entries to mark (the normal
 * HIT-path background revalidate covers that cold sliver).
 */
export function invalidateSwrForModified(payload) {
  const routes = payload && WRITE_INVALIDATES[payload.resourceType];
  if (!routes || !swrStorage) return;
  const profileId = activeLoginProfileId();
  if (!profileId) return; // no stored staff login → no per-user entries to mark
  for (const route of routes) {
    for (const identity of SWR_IDENTITIES) {
      const key = cacheKeyFor(route, identity, profileId);
      try {
        const raw = swrStorage.getItem(key);
        if (!raw) continue;
        const wrapped = JSON.parse(raw);
        if (!wrapped || wrapped.v !== SCHEMA_VERSION || wrapped.stale === true) continue;
        wrapped.stale = true;
        swrStorage.setItem(key, JSON.stringify(wrapped));
      } catch (_) {
        // Corrupt entry / unavailable storage → nothing coherent to mark; the
        // envelope shape guard in readCache treats it as a miss anyway.
      }
    }
  }
}

// Register with the client's write events at module load (this module is loaded
// eagerly via bridge.js, before any screen can write). try/catch — not a typeof
// guard — because vitest's ESM mocks throw on the ACCESS of an export the mock
// factory didn't define, so minimal medplum-client doubles (getMedplum-only)
// would otherwise fail at import. Same defensive shape as invalidateCached's
// test-double method guard; the real module always exports this.
try {
  onResourceModified(invalidateSwrForModified);
} catch (_) { /* test double without the export → no write events to invalidate on */ }

/** "as of HH:MM" from a cache envelope's storedAt (local 24h time). Exported so
 *  a board can render the quiet staleness marker with the SAME formatting
 *  everywhere. Returns '' for a bad timestamp so a caller can `stamp && …`. */
export function formatAsOf(storedAt) {
  const d = new Date(storedAt);
  if (Number.isNaN(d.getTime())) return '';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `as of ${hh}:${mm}`;
}

/**
 * Stale-while-revalidate wrapper for a bridge route loader.
 *
 * Returns the cached payload INSTANTLY when present (so the screen paints from
 * the last authorized read with zero network wait), then runs `loaderFn` in the
 * background; when the fresh result differs from what was served, `onRevalidate`
 * is invoked with the fresh payload so the caller can swap it in.
 *
 * On a cache MISS it awaits `loaderFn` and returns the fresh payload directly
 * (no onRevalidate — the caller already has the freshest data). Either way the
 * returned promise resolves with a payload the caller can render immediately.
 *
 * The identity's profile id is folded into the cache key, so the cache is
 * safe-by-construction: it can only ever return what an authorized loader
 * produced for the same logged-in user + identity.
 *
 * @param {string} cacheKey        the route name, e.g. 'queue' / 'reception'.
 * @param {() => Promise<any>} loaderFn  the existing loadXFromMedplum loader.
 * @param {object} [opts]
 * @param {'doctor'|'receptionist'} [opts.identity='doctor']  medplum-client identity the loader uses.
 * @param {(fresh:any)=>void} [opts.onRevalidate]  called when the background read differs from the served payload.
 * @param {(storedAt:number)=>void} [opts.onStale]  called ONLY when a cache HIT was served but its background
 *        revalidate then FAILED (offline / transient) — the caller surfaces the served data with an "as of
 *        HH:MM" marker. The normal SWR flow (revalidate succeeds) never calls this.
 * @returns {Promise<any>}         a payload to render now (cached if present, else fresh).
 */
// Honest server-reachability signal for the sync chip. navigator.onLine only
// says the DEVICE has a network interface — it is TRUE in an emulator (and on a
// captive wifi) even when the hospital server is unreachable, so a chip driven by
// onLine alone lies "Synced" while the board paints from stale cache. The SWR
// background revalidate is the real probe: it succeeds ⇒ server reachable, it
// throws ⇒ unreachable. We broadcast that so SyncStatusChip can fold it into its
// EXISTING 'offline' state (no new UI vocabulary). Emitted on EVERY background
// outcome so the flag clears cleanly the moment a revalidate succeeds again.
// The last reachability outcome we proved this session, so a sync chip that
// mounts on a screen which never runs an SWR revalidate (consult/prescription)
// can SEED its state from what an earlier board load / warm cycle already
// learned, instead of resetting to an optimistic "reachable" and lying "Synced"
// over stale cache. Module-scope → survives SPA route navigations within the one
// JS context; null until the first emit this session.
let lastReachable = null;

/** The last reachability we proved this session, or null if none yet. Read by the
 *  sync chip to seed its state on mount (see sync-status.jsx useSyncStatus). */
export function getLastReachable() {
  return lastReachable;
}

function emitReachable(ok, storedAt) {
  lastReachable = { ok: !!ok, at: Date.now(), storedAt };
  if (typeof window === 'undefined') return;
  // Stash on window too, so the sync chip can SEED from the last-known value
  // WITHOUT importing this module (it stays a decoupled global widget).
  try { window.__opdLastReachable = lastReachable; } catch (_) { /* non-DOM host */ }
  try { window.dispatchEvent(new CustomEvent('opd:reachable', { detail: { ok, storedAt } })); } catch (_) { /* non-DOM host */ }
}

/**
 * One cheap, one-shot reachability probe for the sync chip's COLD case: a chip
 * that mounts before any board load has proved reachability this session (e.g. a
 * deep-link straight into consult) would otherwise stay optimistically "Synced".
 * We hit the Medplum server's public /.well-known/jwks.json once (208 bytes,
 * unauthenticated, served from memory) — the minimal honest "can my writes reach
 * the hospital cloud right now" signal — and fold the result into the SAME
 * `opd:reachable` event every board revalidate uses, so all chips converge
 * through one channel. NOT polled: the chip fires this only when
 * getLastReachable() is still null (see useSyncStatus), and every later mount
 * seeds from the cached result instead.
 *
 * WHY NOT /healthcheck (2026-07-19 fix): Medplum's CORS layer allowlists only
 * specific path prefixes (/fhir/, /auth/, /.well-known/, … — see
 * medplum-link/packages/server/src/cors.ts) and /healthcheck is NOT one of
 * them, so a browser on the app origin gets a CORS block on the response and
 * the probe threw for a perfectly reachable server. On the deployed demo this
 * made the cold probe emit reachable=false every time — the sync chip claimed
 * "Offline — changes save on this device" on a live connection until the first
 * board load corrected it (and screens with no swrLoad board never corrected).
 *
 * @returns {Promise<boolean|null>} true/false once probed, or null when we can't
 *   even establish a session (an auth gap is NOT unreachability — leave the chip's
 *   default; a real board read will settle it).
 */
export async function probeReachable() {
  // Deliberately NO navigator.onLine short-circuit: WebViews report stale
  // false (device-proven 2026-07-12), and this probe's entire job is to
  // establish the truth. If we are genuinely offline the fetch below throws
  // and we emit false — the same answer, honestly earned.
  let medplum;
  try {
    ({ medplum } = await getMedplum());
  } catch (_) {
    return null; // no live session → not our signal to give; don't claim offline
  }
  try {
    await medplum.get(new URL('.well-known/jwks.json', medplum.getBaseUrl()), { cache: 'no-cache' });
    emitReachable(true);
    return true;
  } catch (_) {
    emitReachable(false);
    return false;
  }
}

// The sync chip (a decoupled global widget that must not import this module)
// asks for the cold-case probe by dispatching 'opd:probe-reachable'. We own the
// probe here; registered once at module load in a DOM host. In the app this
// module is loaded eagerly (bridge.js), so the listener is in place before any
// chip mounts; where it isn't (a stubbed test harness), the event simply no-ops.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('opd:probe-reachable', () => { probeReachable().catch(() => {}); });
}

export async function swrLoad(cacheKey, loaderFn, opts = {}) {
  const { identity = 'doctor', onRevalidate, onStale } = opts;

  // Resolve the identity's real profile id up front so the key is per-user.
  // getMedplum() is the same singleton the loader itself awaits, so this adds
  // no extra login — it reuses the already-resolved session.
  let profileId = 'anon';
  try {
    const { medplum } = await getMedplum(identity);
    const profile = medplum.getProfile();
    if (profile && profile.id) profileId = profile.id;
  } catch (_) {
    // If the session can't even be established, fall through to loaderFn, which
    // will throw the same error to the caller (surfaced as the route's designed
    // error state — converted routes have no Express fallback).
  }
  // OFFLINE cold launch: getMedplum() rejects because its /auth/me session-resume
  // can't reach the network, so getProfile() (and thus profileId) is undefined —
  // yet a valid session WAS restored from storage. Resolve the id from that
  // stored login (sync, no network) so the cache key matches the rows persisted
  // online; without this we'd key under 'anon', MISS the durable cache, and the
  // offline loader would throw "Failed to fetch" instead of painting from cache.
  if (profileId === 'anon') {
    const storedId = activeLoginProfileId();
    if (storedId) profileId = storedId;
  }

  // On device the SWR store is an encrypted SQLite DB that opens + warms its
  // in-memory mirror ASYNCHRONOUSLY. Await that initial warm before reading, or a
  // cold OFFLINE boot reads the still-empty mirror, MISSES, runs the loader, and
  // throws "Failed to fetch" even though the durable cache rows are milliseconds
  // away. Web localStorage (and any store without whenWarm) is synchronous → no
  // wait, byte-identical. This runs once per launch; after warm it resolves
  // instantly.
  if (swrStorage && typeof swrStorage.whenWarm === 'function') {
    try { await swrStorage.whenWarm(); } catch (_) { /* degraded store → treat as a miss */ }
  }

  const key = cacheKeyFor(cacheKey, identity, profileId);
  const cached = readCache(key);

  if (!cached) {
    // MISS: no instant paint possible — await the real read, cache it, return.
    const fresh = await loaderFn();
    writeCache(key, fresh);
    return fresh;
  }

  if (cached.stale === true) {
    // INVALIDATED-BY-WRITE (invalidateSwrForModified): a write through
    // MedplumClient changed a resource this board renders, so painting the
    // cached payload would show the user data they JUST changed away from.
    // FETCH-FIRST: await the fresh read; the marked payload is only the
    // offline fallback (kept, never deleted — the cache stays the boards'
    // offline read store).
    try {
      const fresh = await loaderFn();
      writeCache(key, fresh); // unmarked envelope — back to normal SWR
      emitReachable(true);
      // Same contract as the normal HIT's background refresh: consumers that
      // listen via onRevalidate (bridge.js installs fresh into __OPD_DATA__
      // there) must see the fresh payload too, not just the direct caller.
      if (JSON.stringify(fresh) !== JSON.stringify(cached.payload) && typeof onRevalidate === 'function') {
        try { onRevalidate(fresh); } catch (_) { /* a screen's swap error must not break SWR */ }
      }
      return fresh;
    } catch (e) {
      console.warn('[swr] post-write refresh failed for', cacheKey, '— serving last-known payload —', e && e.message);
      emitReachable(false, cached.storedAt);
      if (typeof onStale === 'function') {
        try { onStale(cached.storedAt); } catch (_) { /* a screen's stamp error must not break SWR */ }
      }
      return cached.payload; // still marked stale → next online visit refreshes
    }
  }

  // HIT: serve stale immediately, revalidate in the background.
  const servedJson = JSON.stringify(cached.payload);
  loaderFn()
    .then((fresh) => {
      writeCache(key, fresh);
      emitReachable(true);
      // Only nudge the screen if the fresh read actually differs from what we
      // painted — otherwise a re-render/refetch would be pure churn.
      if (JSON.stringify(fresh) !== servedJson && typeof onRevalidate === 'function') {
        try { onRevalidate(fresh); } catch (_) { /* a screen's swap error must not break SWR */ }
      }
    })
    .catch((e) => {
      // Background revalidation failed (offline, RLS change, transient). Keep
      // the stale paint — do NOT wipe the cache, do NOT surface as fatal. Tell
      // the caller so it can quietly stamp the board "as of HH:MM" (the served
      // data is real, just not confirmed-fresh this moment).
      console.warn('[swr] background revalidate failed for', cacheKey, '—', e && e.message);
      emitReachable(false, cached.storedAt);
      if (typeof onStale === 'function') {
        try { onStale(cached.storedAt); } catch (_) { /* a screen's stamp error must not break SWR */ }
      }
    });

  return cached.payload;
}

// ────────────────────────────────────────────────────────────────────────────
// 2. Medplum WebSocket subscriptions → screen refetch nudge
// ────────────────────────────────────────────────────────────────────────────

// Coalesce bursts: a bot sweep / bulk write / many staff acting at once can emit
// many notifications in a moment — fire onEvent AT MOST once per window so the
// screen's refetch runs once instead of N times. Task requires >= 1s.
const DEBOUNCE_MS = 1000;

/**
 * Open Medplum WebSocket subscriptions for the given criteria and nudge the
 * screen when any of them fires. This is the Medplum-native replacement for the
 * SSE nudge: each notification is translated into the SAME {type,id,action,at}
 * payload opd-events.js consumers already expect, then debounced and handed to
 * `onEvent`. The caller's `onEvent` just re-runs its existing quiet refetch
 * (loadXFromMedplum → setState), so NO screen JSX changes are required for the
 * screens that already have that plumbing.
 *
 * `criteria` is an array of Medplum subscription criteria strings — a bare
 * resource type ('Encounter') or a filtered search ('Encounter?status=arrived').
 * The doctor AccessPolicy already permits Subscription?type=websocket&author=%profile
 * and the websocket-subscriptions feature flag is ON, so these subscribe as the
 * logged-in user and are RLS-scoped like any other read.
 *
 * @param {object} args
 * @param {import('@medplum/core').MedplumClient} args.medplum  a resolved client (from getMedplum()).
 * @param {string[]} args.criteria  subscription criteria strings.
 * @param {(payload:{type,id,action,at})=>void} args.onEvent  called (debounced) on any matching notification.
 * @returns {() => void}  cleanup — unsubscribes every criteria and closes the socket.
 */
export function startLiveUpdates({ medplum, criteria, onEvent }) {
  if (!medplum || !Array.isArray(criteria) || criteria.length === 0 || typeof onEvent !== 'function') {
    return () => {};
  }
  // No global WebSocket (very old browser / SSR) → no-op; the screen's poll
  // fallback covers it, and we don't pretend to be live.
  if (typeof window === 'undefined' || typeof window.WebSocket === 'undefined') {
    return () => {};
  }

  let closed = false;
  let warned = false;
  let debounceTimer = null;
  let lastPayload = null;
  const emitters = [];

  const fire = (payload) => {
    lastPayload = payload;
    if (debounceTimer) return;
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      const p = lastPayload; lastPayload = null;
      try { onEvent(p); } catch (_) { /* a screen's refresh error must not kill the stream */ }
    }, DEBOUNCE_MS);
  };

  // Translate a Medplum notification Bundle into the {type,id,action,at} shape
  // the existing consumers read. entry[0] is the SubscriptionStatus; entry[1]
  // (when present) is the changed resource.
  const onMessage = (event) => {
    if (closed) return;
    const bundle = event && event.payload;
    const changed = bundle && bundle.entry && bundle.entry[1] && bundle.entry[1].resource;
    if (!changed) return; // heartbeat/handshake bundle with no resource → ignore.
    fire({ type: changed.resourceType, id: changed.id, action: 'change', at: new Date().toISOString() });
  };

  const onError = () => {
    if (closed || warned) return;
    warned = true;
    // Fail loud, ONCE. MedplumClient's ReconnectingWebSocket owns reconnection;
    // we do NOT layer our own retry loop (no storm). The screen stays static on
    // its poll fallback until the socket recovers on its own.
    console.warn('[live] Medplum subscription socket error — live updates paused, screen stays on its poll fallback.');
  };

  // Lightweight observability (window.__OPD_LIVE): connection lifecycle +
  // event counters, so "is the live channel actually working?" is answerable
  // from any open console without a debugger. Deliberately tiny — counters
  // only, never render data.
  const dbg = (typeof window !== 'undefined')
    ? (window.__OPD_LIVE = window.__OPD_LIVE || { subscribed: [], events: 0, lastEventAt: null, opens: 0, closes: 0, heartbeats: 0 })
    : null;

  for (const c of criteria) {
    try {
      const emitter = medplum.subscribeToCriteria(c);
      emitter.addEventListener('message', (event) => {
        if (dbg) { dbg.events += 1; dbg.lastEventAt = new Date().toISOString(); }
        onMessage(event);
      });
      emitter.addEventListener('error', onError);
      emitter.addEventListener('open', () => { if (dbg) dbg.opens += 1; });
      emitter.addEventListener('close', () => { if (dbg) dbg.closes += 1; });
      emitter.addEventListener('heartbeat', () => { if (dbg) dbg.heartbeats += 1; });
      emitters.push({ criteria: c, emitter });
      if (dbg) dbg.subscribed.push(c);
      console.info('[live] subscribed:', c);
    } catch (e) {
      if (!warned) { warned = true; console.warn('[live] could not subscribe to', c, '—', e && e.message); }
    }
  }

  // Clean up on pagehide (tab close / bfcache) so we don't leak sockets.
  const onPageHide = () => cleanup();
  window.addEventListener('pagehide', onPageHide);

  function cleanup() {
    if (closed) return;
    closed = true;
    if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null; }
    for (const { criteria: c, emitter } of emitters) {
      try {
        emitter.removeEventListener('message', onMessage);
        emitter.removeEventListener('error', onError);
        medplum.unsubscribeFromCriteria(c); // drops ref count; closes WS at zero.
      } catch (_) { /* best-effort teardown */ }
    }
    try { window.removeEventListener('pagehide', onPageHide); } catch (_) {}
  }

  return cleanup;
}

// ────────────────────────────────────────────────────────────────────────────
// 3. Screen-owned live wire (Tasks / Messaging)
// ────────────────────────────────────────────────────────────────────────────
//
// The list boards that bootstrap via bridge.js get their WS wire from
// startLiveForRoute (bridge.js). Tasks and Messaging DON'T — they bootstrap on
// the 'settings' payload (currentUser/hospital only) and load their data
// in-screen, so nothing was ever opening a Task/Communication socket for them.
// Their existing `subscribeOpdEvents(['Task'|'Communication'])` listeners were
// therefore DEAD (the OPD_LOCAL_EVENT they listen for is only dispatched by a
// live wire). ensureLiveWire is the screen-callable equivalent of
// startLiveForRoute: it opens the socket ONCE per page load and relays every
// change through the SAME invalidate → OPD_LOCAL_EVENT path bridge.js uses, so
// those in-screen subscribers finally fire on a server push. Idempotent per
// criteria-set (a second call for an already-wired set is a no-op) and it
// tears itself down on pagehide via startLiveUpdates.
const _liveWires = new Set();
export function ensureLiveWire({ criteria } = {}) {
  if (!Array.isArray(criteria) || criteria.length === 0) return () => {};
  const sig = criteria.slice().sort().join(',');
  if (_liveWires.has(sig)) return () => {};
  _liveWires.add(sig);
  let cleanup = () => {};
  // Unmount race (design §1d): under SPA nav the returned cleanup can run BEFORE
  // getMedplum() resolves. Without this flag the wire would then start AFTER
  // cleanup and leak a socket whose `sig` is already deleted. `disposed` makes a
  // late-resolving start a no-op, and a late start's own teardown still runs.
  let disposed = false;
  getMedplum().then(({ medplum }) => {
    if (disposed) return;
    cleanup = startLiveUpdates({
      medplum,
      criteria,
      onEvent: (payload) => {
        // Same two steps bridge.js's startLiveForRoute does: bust MedplumClient's
        // 60s request cache for the changed type (or the refetch below would read
        // the SAME stale search and mask the change), then nudge the screen's
        // in-place subscribeOpdEvents consumer.
        try { medplum.invalidateSearches(payload.type); } catch (_) {}
        try { window.dispatchEvent(new CustomEvent(OPD_LOCAL_EVENT, { detail: payload })); } catch (_) {}
      },
    });
  }).catch(() => { /* no session → no wire; the screen keeps its poll fallback */ });
  return () => { disposed = true; try { cleanup(); } catch (_) {} _liveWires.delete(sig); };
}
